import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Play,
  CheckCircle2,
  Layers,
  Users,
  Target,
  Store,
  BarChart3,
  Headset,
  FileText,
  Database,
  TrendingUp,
  Sparkles,
  ShieldCheck,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Reveal, Chapter } from "@/components/Shared";
import VideoModal from "@/components/VideoModal";

const PROFILE_IMAGES = [
  "https://images.unsplash.com/photo-1556740738-b6a63e27c4df?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1587560699334-cc4ff634909a?auto=format&fit=crop&w=800&q=80",
];

const PROFILE_THEMES = [
  {
    tag: "text-emerald-600",
    icon: "bg-emerald-100 text-emerald-600",
    check: "text-emerald-500",
    card: "bg-emerald-50/60 border-emerald-100",
    footer: "text-emerald-700",
    chip: "bg-emerald-500",
  },
  {
    tag: "text-blue-600",
    icon: "bg-blue-100 text-blue-600",
    check: "text-blue-500",
    card: "bg-blue-50/60 border-blue-100",
    footer: "text-blue-700",
    chip: "bg-blue-600",
  },
  {
    tag: "text-violet-600",
    icon: "bg-violet-100 text-violet-600",
    check: "text-violet-500",
    card: "bg-violet-50/60 border-violet-100",
    footer: "text-violet-700",
    chip: "bg-violet-500",
  },
];

const PROFILE_ICONS = [Store, BarChart3, Headset];
const FEATURE_ICONS = [CheckCircle2, BarChart3, Users, FileText, Database, TrendingUp];
const FEATURE_TILES = [
  "bg-emerald-100 text-emerald-600",
  "bg-blue-100 text-blue-600",
  "bg-violet-100 text-violet-600",
  "bg-amber-100 text-amber-600",
  "bg-rose-100 text-rose-600",
  "bg-orange-100 text-orange-600",
];
const BADGE_ICONS = [Layers, Users, Target];

const SolutionPage = () => {
  const { t } = useLanguage();
  usePageTitle(t.nav.solution);
  const s = t.solution;
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <main data-testid="solution-page" className="pt-[76px]">
      <section
        data-testid="solution-hero"
        className="relative overflow-hidden bg-gradient-to-b from-blue-50/80 via-white to-white py-16 lg:py-24"
      >
        <div className="pointer-events-none absolute inset-0 bg-grid-light opacity-60 [mask-image:radial-gradient(ellipse_at_top,black_20%,transparent_70%)]" />
        <div className="pointer-events-none absolute -top-32 right-[-8%] h-[480px] w-[480px] rounded-full bg-emerald-400/10 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 lg:grid-cols-[1.05fr_1fr] lg:px-8">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid="solution-eyebrow"
              className="inline-block text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-500 sm:text-xs"
            >
              {s.eyebrow}
            </motion.p>
            <h1
              data-testid="solution-title"
              className="mt-5 font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-slate-900 sm:text-5xl lg:text-[3.4rem]"
            >
              {s.titleLines.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${i === s.blueLine ? "text-blue-600" : ""}`}
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
              data-testid="solution-subtitle"
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              {s.subtitle}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/demo"
                data-testid="solution-demo-button"
                className="group flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-600/25 transition-all duration-300 hover:bg-blue-700"
              >
                {s.ctaDemo}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <button
                data-testid="solution-video-button"
                onClick={() => setVideoOpen(true)}
                className="group flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-6 py-3.5 text-sm font-bold text-slate-700 transition-colors duration-300 hover:border-slate-300 hover:bg-slate-50"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 transition-transform duration-300 group-hover:scale-110">
                  <Play className="ml-0.5 h-3 w-3 fill-white text-white" />
                </span>
                {s.ctaVideo}
              </button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.05 }}
              className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3"
              data-testid="solution-badges"
            >
              {s.badges.map((badge, i) => {
                const Icon = BADGE_ICONS[i];
                return (
                  <span key={badge} className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-50">
                      <Icon className="h-3.5 w-3.5 text-emerald-500" />
                    </span>
                    {badge}
                  </span>
                );
              })}
            </motion.div>
          </div>

          <div className="relative flex items-center justify-center gap-4 lg:gap-5" data-testid="solution-hero-cards">
            {s.cards.map((card, i) => {
              const theme = PROFILE_THEMES[i];
              return (
                <motion.div
                  key={card.tag}
                  initial={{ opacity: 0, y: 60, rotate: 0 }}
                  animate={{ opacity: 1, y: 0, rotate: (i - 1) * 5 }}
                  transition={{ duration: 0.9, delay: 0.4 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                  className={`relative w-1/3 ${i === 1 ? "-translate-y-6" : i === 0 ? "translate-y-4" : "translate-y-6"}`}
                >
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 5 + i, repeat: Infinity, ease: "easeInOut" }}
                    className="overflow-hidden rounded-2xl bg-white shadow-[0_28px_56px_-16px_rgba(10,15,29,0.28)]"
                  >
                    <div className="relative h-44 lg:h-56">
                      <img
                        src={PROFILE_IMAGES[i]}
                        alt={card.tag}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1D]/70 to-transparent" />
                    </div>
                    <div className={`border-t-4 bg-white px-4 py-3.5 ${theme.chip.replace("bg-", "border-")}`}>
                      <p className={`text-[10px] font-extrabold uppercase tracking-[0.16em] ${theme.tag}`}>
                        {card.tag}
                      </p>
                      <p className="mt-0.5 font-display text-sm font-bold text-slate-900 lg:text-base">
                        {card.label}
                      </p>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
            <motion.p
              initial={{ opacity: 0, rotate: 0 }}
              animate={{ opacity: 1, rotate: -4 }}
              transition={{ duration: 0.7, delay: 1.5 }}
              data-testid="solution-annotation"
              className="font-hand absolute -bottom-12 right-0 text-xl font-semibold text-slate-700 lg:text-2xl"
            >
              {s.annotation}
            </motion.p>
          </div>
        </div>
      </section>

      <section data-testid="profiles-section" className="py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Chapter index="01" label={s.profilesChapter} />
            <h2 className="max-w-2xl font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl text-balance">
              {s.profilesTitle}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {s.profiles.map((profile, i) => {
              const theme = PROFILE_THEMES[i];
              const Icon = PROFILE_ICONS[i];
              return (
                <Reveal key={profile.tag} delay={i * 0.12} className="h-full">
                  <div
                    data-testid={`profile-card-${i}`}
                    className={`flex h-full flex-col rounded-2xl border p-8 transition-transform duration-500 hover:-translate-y-1.5 ${theme.card}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${theme.icon}`}>
                        <Icon className="h-5 w-5" />
                      </span>
                      <div>
                        <p className={`text-[10px] font-extrabold uppercase tracking-[0.16em] ${theme.tag}`}>
                          {profile.tag}
                        </p>
                        <h3 className="font-display text-lg font-bold text-slate-900">{profile.name}</h3>
                      </div>
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-slate-600">{profile.desc}</p>
                    <ul className="mt-5 flex-1 space-y-2.5">
                      {profile.list.map((item) => (
                        <li key={item} className="flex items-start gap-2.5 text-sm font-medium text-slate-700">
                          <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${theme.check}`} />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6 flex items-center gap-3.5 border-t border-slate-900/5 pt-5">
                      <img
                        src={PROFILE_IMAGES[i]}
                        alt={profile.author}
                        loading="lazy"
                        className="h-11 w-11 rounded-full object-cover ring-2 ring-white"
                      />
                      <div>
                        <p className="text-[13px] italic leading-snug text-slate-600">“{profile.quote}”</p>
                        <p className="mt-1 text-[11px] font-bold text-slate-900">{profile.author}</p>
                      </div>
                    </div>
                    <p className={`mt-5 flex items-start gap-2 text-[13px] font-bold leading-snug ${theme.footer}`}>
                      <ArrowRight className="mt-0.5 h-4 w-4 shrink-0" />
                      {profile.footer}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="flow-section" className="border-y border-slate-100 bg-white py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="relative text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-500 sm:text-xs">
              {s.flow.eyebrow}
            </p>
            <h2 className="mx-auto mt-4 max-w-2xl font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl text-balance">
              {s.flow.title}
            </h2>
            <p
              data-testid="flow-annotation"
              className="font-hand mx-auto mt-5 max-w-xs rotate-[-2deg] text-xl font-semibold text-amber-500 lg:absolute lg:right-4 lg:top-0 lg:mt-0 lg:text-2xl"
            >
              {s.flow.annotation}
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-16 flex flex-col items-center gap-6 lg:flex-row lg:items-stretch lg:justify-center lg:gap-0">
              {s.flow.steps.map((step, i) => {
                const theme = PROFILE_THEMES[i];
                const Icon = PROFILE_ICONS[i];
                return (
                  <div key={step.label} className="flex flex-col items-center gap-6 lg:flex-row lg:gap-0">
                    <div
                      data-testid={`flow-step-${i}`}
                      className="relative flex w-64 flex-col items-center rounded-2xl border border-slate-100 bg-white p-7 text-center shadow-[0_16px_40px_-20px_rgba(10,15,29,0.15)]"
                    >
                      <span
                        className={`absolute -top-3.5 left-1/2 flex h-7 w-7 -translate-x-1/2 items-center justify-center rounded-full font-mono-orbit text-xs font-bold text-white ${theme.chip}`}
                      >
                        {i + 1}
                      </span>
                      <span className={`flex h-12 w-12 items-center justify-center rounded-full ${theme.icon}`}>
                        <Icon className="h-5 w-5" />
                      </span>
                      <p className="mt-4 font-display text-sm font-extrabold uppercase tracking-wider text-slate-900">
                        {step.label}
                      </p>
                      <p className="mt-1.5 text-xs font-semibold text-slate-500">{step.actions}</p>
                    </div>
                    {i < s.flow.steps.length - 1 && (
                      <div className="flex items-center lg:mx-4">
                        <ArrowRight className="h-5 w-5 rotate-90 text-slate-300 lg:rotate-0" />
                        <img
                          src="/assets/orbit-logo.png"
                          alt="Orbit"
                          className="mx-2 hidden h-6 w-auto opacity-90 lg:block"
                        />
                        <ArrowRight className="hidden h-5 w-5 text-slate-300 lg:block" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="relative mt-20">
              <div className="absolute left-0 right-0 top-5 hidden border-t-2 border-dashed border-slate-200 lg:block" />
              <div className="relative grid gap-8 sm:grid-cols-3 lg:grid-cols-5">
                {s.flow.process.map((step, i) => (
                  <div key={step} data-testid={`process-step-${i}`} className="flex items-start gap-4 lg:flex-col lg:items-center lg:text-center">
                    <span className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-900 font-mono-orbit text-sm font-bold text-white ring-4 ring-white">
                      {i + 1}
                    </span>
                    <p className="text-sm font-medium leading-snug text-slate-600 lg:mt-3 lg:max-w-[180px]">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="solution-features-section" className="bg-slate-50/70 py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Chapter index="02" label={s.features.eyebrow} />
            <h2 className="max-w-2xl font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl text-balance">
              {s.features.title}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {s.features.items.map((feature, i) => {
              const Icon = FEATURE_ICONS[i];
              return (
                <Reveal key={feature.title} delay={i * 0.08}>
                  <div
                    data-testid={`solution-feature-${i}`}
                    className="group h-full rounded-2xl border border-slate-100 bg-white p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-slate-200 hover:shadow-[0_24px_48px_-16px_rgba(10,15,29,0.12)]"
                  >
                    <span
                      className={`flex h-12 w-12 items-center justify-center rounded-xl transition-transform duration-500 group-hover:scale-110 ${FEATURE_TILES[i]}`}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{feature.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{feature.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div
              data-testid="solution-cta"
              className="relative flex flex-col items-start justify-between gap-8 overflow-hidden rounded-2xl bg-[#0A0F1D] p-9 lg:flex-row lg:items-center lg:p-14"
            >
              <div className="pointer-events-none absolute inset-0 bg-grid-navy" />
              <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-emerald-500/15 blur-3xl" />
              <div className="relative">
                <h2 className="font-display text-2xl font-bold text-white lg:text-3xl text-balance">{s.cta.title}</h2>
                <p className="mt-3 text-sm text-slate-400 sm:text-base">{s.cta.text}</p>
              </div>
              <Link
                to="/demo"
                data-testid="solution-cta-button"
                className="group relative inline-flex shrink-0 items-center gap-2 rounded-xl bg-blue-600 px-7 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-blue-500"
              >
                {s.cta.button}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <VideoModal
        open={videoOpen}
        onOpenChange={setVideoOpen}
        title={t.hero.videoTitle}
        testid="solution-video-modal"
      />
    </main>
  );
};

export default SolutionPage;
